import React, { ReactNode } from "react"
import styled, {
  css,
  FlattenInterpolation,
  ThemeProps,
} from "styled-components"
import { Image } from "../image.component"
import { BasicButtonLink } from "../../../ui/components/link/link.component"
import { AppFunctionComponent } from "../../../types"

export enum HeroDesktopLayout {
  CenteredTexts,
  SeparatedTexts,
  CenteredSingleColumn,
  LandingPageWithFullWidth,
  BlackCenteredSingleColumnFullWidth,
  HeadingInLineLayout,
  SeparatedTextsKompakt,
  ImageTextKompakt,
}

export enum HeroMobileLayout {
  HeadingImageText,
  Fit,
  Custom,
  Fill,
  CenteredSingleColumn,
  ImageHeadingText,
  TextOnImage,
}

export enum HeroImageBehavior {
  Cover,
  Full,
  Fit,
  Fill,
  Contain,
  OriginalSize,
}

interface HeroLayoutProps {
  mobileLayout?: HeroMobileLayout | FlattenInterpolation<ThemeProps<any>>
  desktopLayout: HeroDesktopLayout | FlattenInterpolation<ThemeProps<any>>
  imageBehavior?: HeroImageBehavior | FlattenInterpolation<ThemeProps<any>>
  submenu?: ReactNode
  small?: boolean
  children?: React.ReactNode
  className?: string | undefined
}

export const HeroHeading = styled.div`
  grid-area: Heading;
  position: relative;
  z-index: 2;
`

export const HeroText = styled.div`
  grid-area: Text;
  position: relative;
  z-index: 2;
`

export const HeroImage = styled(Image)`
  grid-area: Image;
  position: relative;
  z-index: 1;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    width: auto;
    height: auto;
  }
`

export const heroImageBehaviorCover = css`
  ${HeroImage} {
    img {
      object-fit: cover;
      object-position: center;
    }
  }
`

export const heroImageOriginalSize = css`
  ${HeroImage} {
    img {
      object-fit: contain !important;
    }
  }
`

export const heroImageBehaviorFit = css`
  ${heroImageBehaviorCover};

  ${HeroImage} {
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    grid-area: auto;
    position: absolute;

    img {
      right: 0;
      left: auto;
      width: auto;
    }
  }
`

export const heroImageBehaviorFill = css`
  ${heroImageBehaviorFit};

  ${HeroImage} {
    height: 90%;
    right: 50px;
  }
`

export const heroImageBehaviorContain = css`
  ${heroImageBehaviorCover}

  ${HeroImage} {
    height: 100%;
    right: 0;
    grid-area: Image;
    overflow-x: visible;
    overflow-y: visible;
  }
`

export const heroImageBehaviorFull = css`
  ${heroImageBehaviorFit};

  ${HeroImage} {
    img {
      width: 100%;
    }
  }
`

const generalMobileStyles = css`
  ${HeroHeading}, ${HeroText} {
    text-align: center;
    justify-self: center;
  }
  ${HeroImage} {
    height: calc(100vw - 40px);
    margin: 0 20px;
    border-radius: 8px;
  }
  ${heroImageBehaviorCover};
`

export const mobileHeadingImageTextLayout = css`
  ${generalMobileStyles};
  grid-template-areas:
    ". Heading ."
    "Image Image Image"
    ". Text .";
  grid-template-columns: 20px 1fr 20px;

  ${HeroHeading} {
    padding: var(--mobile-padding) 0;

    > *:first-child {
      margin-top: 0;
    }

    > *:last-child {
      margin-bottom: 0;
    }
  }

  ${HeroText} {
    width: 100%;
    text-align: left;
    padding-top: var(--mobile-padding);
    padding-bottom: var(--mobile-padding);

    > *:first-child {
      margin-top: 0;
    }

    > *:last-child {
      margin-bottom: 0;
    }
  }
`

export const mobileFitLayout = css`
  ${mobileHeadingImageTextLayout};
  ${HeroText} {
    text-align: var(--mobile-text-align);
    a {
      background: var(--button-color);
    }
  }
  ${HeroHeading} {
    padding-bottom: var(--mobile-hero-padding);
    text-align: var(--mobile-text-align);
    h1 {
      color: var(--link-color);
      font-weight: var(--header-text-font-weight);
      font-size: var(--header-main-text-font-size);
      margin: var(--mobile-margin);
    }
    p {
      font-size: var(--header-text-font-size);
      font-weight: var(--header-text-font-weight);
      margin-top: var(--mobile-margin-top);
      line-height: var(--mobile-hero-header-paragraph-line-height);
    }
  }
  ${HeroImage} {
    height: auto;
    max-height: 550px;
  }
`

export const mobileCustomLayout = css`
  ${mobileHeadingImageTextLayout};
  justify-items: center;
  background: #fff;
  grid-template-areas:
    "Heading Heading Heading"
    "Text Text Text"
    "Image Image Image";
  padding-left: 20px;
  padding-right: 20px;
  ${HeroText} {
    text-align: left;
    padding-top: 24px;
    padding-bottom: 20px;
    h3 {
      margin: 12px 0;
    }
    p {
      margin: 12px 0;
    }
    a {
      background: var(--button-color);
    }
  }
  ${HeroHeading} {
    padding-bottom: var(--mobile-hero-padding);
    text-align: var(--mobile-text-align);
    justify-self: flex-start;
    h1 {
      margin: var(--mobile-margin);
      text-align: left;
    }
    p {
      font-size: var(--header-text-font-size);
      font-weight: var(--header-text-font-weight);
      margin-top: var(--mobile-margin-top);
      line-height: var(--mobile-hero-header-paragraph-line-height);
    }
  }
  ${HeroImage} {
    height: 300px;
    max-height: 400px;
    border-radius: 8px;
    img {
      object-fit: cover;
      object-position: center;
    }
  }
`

export const mobileImageHeadingText = css`
  ${mobileCustomLayout}
  grid-template-areas:
    "Image Image Image"
    "Heading Heading Heading"
    "Text Text Text";

  ${HeroHeading} {
    width: 100%;
    h1,
    p {
      text-align: center;
    }
  }

  h1,
  p {
    text-align: center;
  }

  ${HeroText} {
    ${BasicButtonLink} {
      display: block;
      width: 240px;
      background-color: #393939;
      margin-left: auto;
      margin-right: auto;
      margin-top: 2rem;
    }
  }

  ${HeroImage} {
    height: 200px;
    max-height: 241px;
    border-radius: 8px;
  }
`

export const mobileTextOnImage = css`
  ${mobileHeadingImageTextLayout}
  background: #F7F7F7;
  padding: 40px 0;
  grid-template-areas:
    "Heading Heading Heading"
    "Text Text Text"
    "Image Image Image";

  @media (min-width: 400px) {
  }

  ${HeroText} {
    padding: 0;
    padding-top: 20px;
    padding-bottom: 32px;
  }
  ${HeroHeading} {
    padding: 0;
  }

  h2,
  p {
    color: black;
  }

  h2 {
    color: black;
    text-align: center;
    font-family: Facto;
    font-size: 30px;
    font-style: normal;
    font-weight: 300;
    line-height: 120%; /* 36px */
    margin: 0;
    strong {
      font-weight: 400;
    }
  }

  p strong {
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 150%;
  }

  ${HeroImage} {
    img {
      object-fit: contain;
    }
  }
`

export const mobileFixedLayoutHazelLP = css`
  ${mobileHeadingImageTextLayout}
  display: block;
  background: #f7f7f7;
  padding: 40px 20px;
  grid-template-areas:
    "Heading Heading Heading"
    "Text Text Text"
    "Image Image Image";

  position: relative;

  height: 800px;

  @media (min-width: 400px) {
  }

  ${HeroText} {
    /* padding: 0; */
    padding-top: 10px;
    padding-bottom: 32px;
  }
  ${HeroHeading} {
    /* padding: 0; */
    padding-top: 32px;
    padding-bottom: 0;
    max-height: 100px;
    max-width: 250px !important;
  }

  h1,
  h2,
  p {
    color: black;
    text-align: center;
  }

  h1 {
    font-family: Facto;
    font-size: 30px;
    font-weight: 400;
    line-height: 36px;
    text-align: center;
  }

  h2 {
    color: black;
    text-align: center;
    font-family: Facto;
    font-size: 30px;
    font-style: normal;
    font-weight: 300;
    line-height: 120%; /* 36px */
    margin: 0;
    strong {
      font-weight: 400;
    }
  }

  p strong {
    font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 150%;
  }

  ${HeroImage} {
    position: absolute;
    height: 100%;
    width: 100%;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    img {
      object-fit: cover;
    }
  }
`

export const mobileCustomLayoutWhiteText = css`
  ${mobileHeadingImageTextLayout};
  justify-items: center;
  grid-template-areas:
    "Heading Heading Heading"
    "Text Text Text"
    "Image Image Image";
  padding-left: 20px;
  padding-right: 20px;
  ${HeroText} {
    text-align: center;
    padding-top: 10px;
    padding-bottom: 20px;
    h3 {
      margin: 0;
      color: var(--text-color);
    }
    p {
      margin: 0;
      color: var(--text-color);
    }
    a {
      display: flex;
      padding: 12px 24px;
      justify-content: center;
      align-items: center;
      gap: 8px;
      border-radius: 5px;
      border: 1px solid var(--button-border-color);
      background: var(--button-color);
      width: max-content;
      margin: auto;
      margin-top: 30px;
      max-width: 60%;
      width: 100%;
    }
  }
  ${HeroHeading} {
    padding-bottom: 15px;
    text-align: var(--mobile-text-align);
    justify-self: center;
    h1 {
      margin: var(--mobile-margin);
      text-align: center;
      color: var(--link-color);
      font-size: 30px;
      font-style: normal;
      font-weight: 400;
      line-height: 120%;
    }
    p {
      font-size: var(--header-text-font-size);
      font-weight: var(--header-text-font-weight);
      margin-top: var(--mobile-margin-top);
      line-height: var(--mobile-hero-header-paragraph-line-height);
    }
  }

  ${HeroImage} {
    height: 190px;
    max-height: 400px;
    margin-top: 40px;
    border-radius: 8px;

    img {
      object-fit: cover;
    }
  }
`

const mobileFillLayout = css`
  ${mobileHeadingImageTextLayout};
  ${HeroHeading} {
    p {
      color: var(--link-color);
    }
  }
  ${HeroImage} {
    height: auto;
    max-height: 535px;
    max-width: 90%;
    margin-left: 5%;
    img {
      object-fit: contain;
      object-position: center;
    }
  }
`

export const generalDesktopStyles = css`
  grid-template-columns: var(--columns-ratio) 1fr;
  ${HeroText}, ${HeroHeading} {
    padding-left: 15px;
    padding-right: 1em;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    grid-template-columns: var(--columns-ratio) 1fr;
    ${HeroText}, ${HeroHeading} {
      padding-left: 30px;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    grid-template-columns: var(--columns-ratio) 1fr;
    ${HeroText}, ${HeroHeading} {
      padding-left: 60px;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.desktop}px) {
    grid-template-columns: var(--columns-ratio) 1fr;
    ${HeroText}, ${HeroHeading} {
      padding-left: 100px;
    }
  }
`

export const desktopCenteredTextsLayout = css`
  ${generalDesktopStyles};
  grid-template-areas:
    ". Image"
    "Heading Image"
    "Text Image"
    ". Image";
  grid-template-rows: 1fr auto auto 1fr;

  ${HeroHeading} {
    h1 {
      color: var(--heading-text-color);
    }
    h2 {
      color: var(--text-color);
    }
    h3 {
      color: var(--text-color);
    }
    h4 {
      color: var(--text-color);
    }
  }
  ${HeroText} {
    h2 {
      color: var(--text-color);
    }
    p {
      color: var(--text-color);
    }
  }
`

export const desktopSeparatedTextsLayout = css`
  ${generalDesktopStyles};
  grid-template-columns: var(--columns-ratio) 1fr;
  grid-template-rows: 6vw auto 1fr auto 4vw;
  grid-template-areas:
    ". Image"
    "Heading Image"
    ". Image"
    "Text Image"
    ". Image";

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    grid-template-rows: ${120 / 13.1}vw auto 1fr auto ${90 / 13.1}vw;
  }

  @media (min-width: 1310px) {
    grid-template-rows: 120px auto 1fr auto 90px;
  }

  @media (min-width: 1310px) and (max-height: 800px) {
    grid-template-rows: ${120 / 9}vh auto 1fr auto ${90 / 9}vh;
  }

  @media (min-width: 1310px) and (max-height: 600px) {
    grid-template-rows: 80px auto 1fr auto 60px;
  }

  ${HeroHeading} {
    > *:first-child {
      margin-top: 0;
    }
  }

  ${HeroText} {
    align-self: flex-end;
  }
`

export const desktopSeparatedTextsLayoutKompakt = css`
  ${generalDesktopStyles};
  grid-template-columns: var(--columns-ratio) 1fr;
  grid-template-rows: 6vw auto 1fr auto 4vw;
  grid-template-areas:
    ". Image"
    "Heading Image"
    ". Image"
    "Text Image"
    ". Image";

  max-height: 550px;
  max-width: unset;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    grid-template-rows: ${120 / 13.1}vw auto 1fr auto ${90 / 13.1}vw;
  }

  @media (min-width: 1310px) {
    grid-template-rows: 120px auto 1fr auto 90px;
  }

  @media (min-width: 1310px) and (max-height: 800px) {
    grid-template-rows: ${120 / 9}vh auto 1fr auto ${90 / 9}vh;
  }

  @media (min-width: 1310px) and (max-height: 600px) {
    grid-template-rows: 80px auto 1fr auto 60px;
  }

  h2 {
    color: var(--text-color);
    font-family: Facto;
    font-size: 46px;
    font-weight: 300;
    line-height: 55.2px;
    text-align: left;
    margin: 0;

    strong {
      font-weight: 400;
    }
  }

  p strong {
    color: var(--text-color);
    font-family: Montserrat;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 140%;
    text-align: left;
  }

  ${HeroHeading} {
    > *:first-child {
      margin-top: 0;
    }
    p {
      color: white;
    }
  }

  ${HeroText} {
    align-self: flex-end;
  }
`

export const desktopHeadingInLineLayout = css`
  ${generalDesktopStyles};
  --columns-ratio: 0.8fr;
  grid-template-columns: var(--columns-ratio) 1fr;
  grid-template-rows: 6vw auto 1fr auto 4vw;
  grid-template-areas:
    "Heading Text"
    "Image Image";

  background: #fff;
  width: calc(100% - 60px);
  max-width: 1260px;
  margin: auto;
  height: unset;

  margin-top: 60px;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    grid-template-rows: ${120 / 13.1}vw auto 1fr auto ${90 / 13.1}vw;
  }

  @media (min-width: 1310px) {
    grid-template-rows: 70px auto;
  }

  @media (min-width: 1310px) and (max-height: 800px) {
    grid-template-rows: ${120 / 9}vh auto 1fr auto ${90 / 9}vh;
  }

  @media (min-width: 1310px) and (max-height: 600px) {
    grid-template-rows: 80px auto 1fr auto 60px;
  }

  ${HeroHeading} {
    > *:first-child {
      margin-top: 0;
    }
    align-self: flex-start;
    text-align: left;
    padding: 0;
  }

  ${HeroText} {
    align-self: flex-start;
    text-align: right;
    padding: 0;

    h1 {
      margin: 0;
    }
  }

  ${HeroImage} {
    max-height: 520px;
    border-radius: 8px;
  }
`

export const desktopLandingWithFullWidth = css`
  --columns-ratio: 1fr;
  ${generalDesktopStyles};
  grid-template-rows: 0px 1fr 640px;
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  overflow: visible;
  grid-template-areas:
    ". ."
    "Heading Image"
    "Text Image";
  height: unset;
  max-height: unset;
  margin-top: 3rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    grid-template-rows: 0px auto auto;
  }

  @media (min-width: 1280px) {
    grid-template-rows: 0px auto auto;
  }

  ${HeroHeading} {
    padding-left: 20px;
    h1 {
      margin-top: 0;
      font-weight: 500;
      font-size: 63px;
      line-height: 60px;
      color: var(--link-color);
    }
    p {
      font-size: 37px;
      font-weight: 500;
      line-height: 49.5px;
    }
    p + p,
    li + p {
      margin-top: 1em;
    }
  }

  ${HeroText} {
    text-align: center;
    padding-left: 20px;
    a {
      padding-left: 4.5rem;
      padding-right: 4.5rem;
      margin-top: 5rem;
      background: var(--button-color);
    }
    p {
      font-size: 19px;
      color: var(--text-color);
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
      align-self: flex-start;
      text-align: left;
      margin-top: 3vh;
    }
  }

  @media (min-width: ${({ theme }) =>
      theme.breakpoint.tablet}px) and (max-width: ${({ theme }) =>
      theme.breakpoint.desktopMedium}px) {
    ${HeroText}, ${HeroHeading} {
      padding-left: 50px;
    }
  }
`

export const mobileCenteredSingleColumnLayout = css`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 0px 20px 0;
  ${HeroHeading}, ${HeroText} {
    text-align: center;
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p {
      color: var(--text-color);
    }

    h1 {
      line-height: 120%;
      font-size: 30px;
      font-style: normal;
      font-weight: 400;
      line-height: 120%;
    }

    p {
      line-height: 150%;
    }
  }

  ${HeroImage} {
    height: 400px;
    margin-top: 2rem;
  }
`
export const desktopCenteredSingleColumnLayout = css`
  ${mobileCenteredSingleColumnLayout}

  padding-top: 6vw;
`

export const desktopBlackCenteredSingleColumnLayout = css`
  ${mobileCenteredSingleColumnLayout}

  padding-top: 3vw;

  ${HeroHeading} {
    padding-left: 20px;
    h1 {
      margin-top: 0;
      font-size: 48px;
      font-style: normal;
      font-weight: 400;
      line-height: 120%;
      color: var(--link-color);
      margin-bottom: 1.5rem;
    }
    p {
      font-size: 37px;
      font-weight: 500;
      line-height: 49.5px;
    }
    p + p,
    li + p {
      margin-top: 1em;
    }
  }

  ${HeroText} {
    text-align: center;
    padding-left: 20px;
    padding-top: 20px;
    a {
      padding-left: 4.5rem;
      padding-right: 4.5rem;
      margin-top: 2rem;
      margin-bottom: 2rem;
      background: var(--button-color);
      border: 1px solid var(--button-border-color);
    }
    p {
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
      color: var(--text-color);
    }
    p + p,
    li + p {
      margin-top: 0;
    }
  }
`

const desktopHeroImageText = css`
  ${desktopBlackCenteredSingleColumnLayout}

  max-height: unset;

  ${HeroHeading} {
    order: 1;
  }
  ${HeroText} {
    order: 3;
  }
  ${HeroImage} {
    order: 2;
    height: 100%;
    border-radius: 8px;
  }
`

const desktopHeroImageTextKompakt = css`
  ${desktopBlackCenteredSingleColumnLayout}
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: unset !important;
  overflow: visible;
  height: unset;
  max-height: unset;
  padding: 0%;
  ${HeroHeading} {
    order: 2;
    text-align: center;
    h1 {
      font-family: Facto;
      font-size: 46px;
      font-weight: 300;
      line-height: 55.2px;
      text-align: center;
      margin-top: 30px;
      margin-bottom: 24px;

      strong {
        font-weight: 400;
      }
    }
  }
  ${HeroText} {
    order: 3;
    text-align: center;
    padding-top: unset;
    --text-max-width: 780px;

    p {
      font-family: Montserrat;
      font-size: 18px;
      font-weight: 300;
      line-height: 27px;
      letter-spacing: 0.04em;
      text-align: center;
    }

    a {
      margin-top: 40px;
      background-color: #3b3f42;
      border: unset;
    }
  }
  ${HeroImage} {
    order: 1;
    height: 100%;
    width: 100%;
    border-radius: 8px;
    position: relative !important;
    max-height: 520px;
    overflow: visible;
    right: unset !important;
    display: flex;
    align-items: center;
    margin-top: 1rem;
    margin-bottom: 1rem;
    img {
      /* top: 20%; */
      width: 100% !important;
      overflow: visible;
      max-height: 520px;
    }
  }
`

const smallHero = css`
  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    height: calc(${575 / 13.1}vw);
  }

  @media (min-width: 1310px) and (min-height: 725px) {
    max-height: 575px;
  }
`

const generalHeroStyles = css`
  display: grid;
  overflow: hidden;
  position: relative;
  background-color: inherit;
  min-height: var(--min-hero-height);
  border-radius: ${({ theme }) => theme.size.borderRadius};

  --subheader-height: 0px;
  --header-height: ${({ theme }) => theme.size.headerMobile}px;

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    --header-height: ${({ theme }) => theme.size.headerTablet}px;
    height: calc(50vw);
    max-height: calc(100vh - var(--header-height));
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.tabletHorizontal}px) {
    --header-height: ${({ theme }) => theme.size.headerDesktop}px;
  }

  --mobile-padding: 30px;

  @media (min-height: 500px) and (min-width: 425px) {
    --mobile-padding: 50px;
  }

  @media (min-width: 1360px) and (min-height: 830px) {
    max-height: 680px;
  }
`

const HeroSection = styled.section<HeroLayoutProps>`
  z-index: 5;
  background: var(--background);

  ${generalHeroStyles};

  ${({ small }) => small && smallHero};

  @media (max-width: ${({ theme }) =>
      theme.breakpoint.mobileHorizontal - 1}px) {
    ${({ mobileLayout }) => {
      switch (mobileLayout) {
        case HeroMobileLayout.HeadingImageText:
          return mobileHeadingImageTextLayout
        case HeroMobileLayout.Fit:
          return mobileFitLayout
        case HeroMobileLayout.Custom:
          return mobileCustomLayout
        case HeroMobileLayout.Fill:
          return mobileFillLayout
        case HeroMobileLayout.CenteredSingleColumn:
          return mobileCenteredSingleColumnLayout
        case HeroMobileLayout.ImageHeadingText:
          return mobileImageHeadingText
        case HeroMobileLayout.TextOnImage:
          return mobileFixedLayoutHazelLP
        default:
          return mobileLayout
      }
    }};
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    ${({ desktopLayout }) => {
      switch (desktopLayout) {
        case HeroDesktopLayout.CenteredTexts:
          return desktopCenteredTextsLayout
        case HeroDesktopLayout.SeparatedTexts:
          return desktopSeparatedTextsLayout
        case HeroDesktopLayout.CenteredSingleColumn:
          return desktopCenteredSingleColumnLayout
        case HeroDesktopLayout.LandingPageWithFullWidth:
          return desktopHeroImageText
        case HeroDesktopLayout.BlackCenteredSingleColumnFullWidth:
          return desktopBlackCenteredSingleColumnLayout
        case HeroDesktopLayout.HeadingInLineLayout:
          return desktopHeadingInLineLayout
        case HeroDesktopLayout.SeparatedTextsKompakt:
          return desktopSeparatedTextsLayoutKompakt
        case HeroDesktopLayout.ImageTextKompakt:
          return desktopHeroImageTextKompakt
        default:
          return desktopLayout
      }
    }};

    ${({ imageBehavior }) => {
      switch (imageBehavior) {
        case HeroImageBehavior.Cover:
          return heroImageBehaviorCover
        case HeroImageBehavior.Full:
          return heroImageBehaviorFull
        case HeroImageBehavior.Fit:
          return heroImageBehaviorFit
        case HeroImageBehavior.Fill:
          return heroImageBehaviorFill
        case HeroImageBehavior.Contain:
          return heroImageBehaviorContain
        case HeroImageBehavior.OriginalSize:
          return HeroImageBehavior.OriginalSize
        default:
          return imageBehavior
      }
    }};
  }

  ${HeroHeading} {
    max-width: var(--heading-max-width);
  }

  ${HeroText} {
    max-width: var(--text-max-width);
  }

  ${BasicButtonLink} {
    margin-top: 2em;
    @media (max-width: ${({ theme }) =>
        theme.breakpoint.mobileHorizontal - 1}px) {
      width: 100%;
    }
  }
`

const StandardHeroWrapper = css`
  width: 100%;
  max-width: 1260px;
  justify-self: center;
  margin-left: auto;
  margin-right: auto;
  --columns-ratio: 1.25fr;
  --min-hero-height: 350px;
  /* --background: ${({ theme }) => theme.color.backgroundLightGradient}; */

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    width: calc(100% - 40px);
  }
`
const HeroWrapperFullWidthBackground = css`
  width: 100%;
  max-width: 100%;
  justify-self: center;
  margin-left: auto;
  margin-right: auto;
  --columns-ratio: 1.25fr;
  --min-hero-height: 350px;
  /* background: var(--hero-background-color); */
  display: flex;
  justify-content: center;
  section {
    background: none;
    background-color: none;
    max-width: 1260px;
    max-height: unset;
    height: unset;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    .gatsby-image-wrapper {
      margin-top: 50px;
      border-radius: 12px;
    }
  }
`

const KickstarterHeroWrapper = css`
  width: 100%;
  max-width: 100%;
  justify-self: center;
  margin-left: auto;
  margin-right: auto;
  background: var(--hero-background-color);

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}px) {
    /* width: calc(100% - 40px); */
  }
`

const HeroWrapper = styled.div<HeroLayoutProps>`
  ${({ desktopLayout }) => {
    switch (desktopLayout) {
      case HeroDesktopLayout.LandingPageWithFullWidth:
        return KickstarterHeroWrapper
      case HeroDesktopLayout.BlackCenteredSingleColumnFullWidth:
        return HeroWrapperFullWidthBackground
      default:
        return StandardHeroWrapper
    }
  }}
`

const Hero: AppFunctionComponent<HeroLayoutProps> = ({
  className,
  children,
  mobileLayout = HeroMobileLayout.HeadingImageText,
  desktopLayout,
  imageBehavior,
  submenu,
  small = false,
}) => (
  <HeroWrapper desktopLayout={desktopLayout} className={className}>
    {submenu}
    <HeroSection
      mobileLayout={mobileLayout}
      desktopLayout={desktopLayout}
      imageBehavior={imageBehavior}
      submenu={submenu}
      small={small}
      data-testid="hero"
    >
      {children}
    </HeroSection>
  </HeroWrapper>
)

export default Hero
